import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { paymentAPI } from '../services/api';
import { useToast } from '../hooks/useToast';
import Layout from '../components/Layout';
import Icon from '../components/Icon';
import { COMPANY_BASIC_FREE_CAMPAIGN_END_LABEL, isCompanyBasicFreeCampaignActive } from '../utils/campaigns';
import '../styles/BillingPage.css';

function BillingPage({ user, onLogout, darkMode = true, onToggleDarkMode }) {
  const toast = useToast();
  const location = useLocation();
  const isSuperAdmin = user?.role === 'super_admin';
  
  // Regular user billing state
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(0);
  const [lemonSqueezyData, setLemonSqueezyData] = useState(null);
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const isDarkMode = darkMode;
  const isBasicCampaignActive = isCompanyBasicFreeCampaignActive();
  
  // Super admin billing state
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [billingStats, setBillingStats] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('subscriptions');

  useEffect(() => {
    if (isSuperAdmin) {
      fetchSuperAdminBillingData();
    } else {
      fetchBillingData();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin) return;
    const params = new URLSearchParams(location.search);
    const payment = params.get('payment');
    const expectedPlan = params.get('plan');
    let stopped = false;

    if (payment !== 'processing' && payment !== 'success' && payment !== 'cancelled' && payment !== 'error') {
      return;
    }

    const clearQuery = () => {
      window.history.replaceState({}, '', '/billing');
    };

    const normalizePlan = (plan) => {
      const raw = String(plan || '').toLowerCase();
      const map = {
        monthly: 'basic',
        community: 'basic',
        basic: 'basic',
        yearly: 'professional',
        growth: 'professional',
        pro: 'professional',
        professional: 'professional',
        enterprise: 'enterprise',
      };
      return map[raw] || raw;
    };

    const waitForWebhookSync = async () => {
      if (payment === 'cancelled') {
        toast.error('Payment was cancelled.');
        clearQuery();
        return;
      }
      if (payment === 'error') {
        toast.error('Payment failed. Please try again.');
        clearQuery();
        return;
      }

      const normalizedExpectedPlan = normalizePlan(expectedPlan);
      toast.success('Payment completed. Finalizing your subscription...');
      for (let attempt = 0; attempt < 14; attempt += 1) {
        if (stopped) return;
        try {
          // Fallback for delayed/missing webhook: sync directly from provider.
          await paymentAPI.syncLemonSqueezySubscription(normalizedExpectedPlan);
        } catch (err) {
          // Keep polling; provider data can lag immediately after checkout.
        }
        try {
          const lsRes = await paymentAPI.getLemonSqueezySubscription();
          const sub = lsRes.data?.subscription;
          const isActivated = sub?.status === 'active' || sub?.status === 'trial';
          const hasPlan = !normalizedExpectedPlan || normalizePlan(sub?.plan_tier) === normalizedExpectedPlan;
          if (isActivated && hasPlan) {
            await fetchBillingData();
            toast.success('Subscription updated successfully.');
            clearQuery();
            return;
          }
        } catch (err) {
          // Keep polling; webhook/provider update can lag behind redirect.
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      await fetchBillingData();
      toast.success('Payment received. Subscription sync is in progress.');
      clearQuery();
    };

    waitForWebhookSync();
    return () => {
      stopped = true;
    };
  }, [isSuperAdmin, location.search]);

  const fetchSuperAdminBillingData = async () => {
    try {
      setLoading(true);
      const [subsRes, paymentsRes, invoicesRes] = await Promise.all([
        paymentAPI.getAllSubscriptions(),
        paymentAPI.getAllPayments({ limit: 100 }),
        paymentAPI.getAllInvoices({ limit: 100 }),
      ]);
      
      setAllSubscriptions(subsRes.data.subscriptions || []);
      setBillingStats(subsRes.data.stats || {});
      setAllPayments(paymentsRes.data.payments || []);
      setAllInvoices(invoicesRes.data.invoices || []);
    } catch (error) {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const [lsRes, paymentsRes, invoicesRes] = await Promise.all([
        paymentAPI.getLemonSqueezySubscription(),
        paymentAPI.getPayments(),
        paymentAPI.getInvoices(),
      ]);
      
      setSubscription(lsRes.data.subscription);
      setTrialDaysRemaining(lsRes.data.trial_days_remaining || 0);
      setLemonSqueezyData(lsRes.data.lemon_squeezy);
      setPayments(paymentsRes.data.payments || []);
      setInvoices(invoicesRes.data.invoices || []);
    } catch (error) {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planTier) => {
    setProcessing(true);
    try {
      const response = await paymentAPI.createCheckoutSession(planTier, 'billing');
      if (response.data?.campaign_applied) {
        await fetchBillingData();
        toast.success(response.data?.message || `Basic activated for free campaign until ${COMPANY_BASIC_FREE_CAMPAIGN_END_LABEL}.`);
        return;
      }
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }
      if (response.data?.checkout_required === false) {
        await fetchBillingData();
        toast.success('Plan activated successfully.');
        return;
      }
      toast.error('Checkout URL is missing');
      setProcessing(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to start checkout');
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access at the end of the current billing period.')) {
      return;
    }

    setProcessing(true);
    try {
      await paymentAPI.cancelLemonSqueezySubscription();
      toast.success('Subscription cancelled. You will have access until the end of your billing period.');
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleResume = async () => {
    setProcessing(true);
    try {
      await paymentAPI.resumeLemonSqueezySubscription();
      toast.success('Subscription resumed successfully!');
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to resume subscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleSwitchPlan = async (newPlan) => {
    setProcessing(true);
    setShowSwitchModal(false);
    const targetPlan = String(newPlan || '').toLowerCase();

    // Campaign shortcut: Basic plan free during campaign period (no provider needed)
    if (targetPlan === 'basic' && isBasicCampaignActive) {
      try {
        const response = await paymentAPI.createCheckoutSession(targetPlan, 'billing');
        if (response.data?.campaign_applied) {
          await fetchBillingData();
          toast.success(response.data?.message || `Basic activated for free campaign until ${COMPANY_BASIC_FREE_CAMPAIGN_END_LABEL}.`);
          return;
        }
      } catch (err) {
        // Fall through to normal flow
      }
    }

    // If user has an active LemonSqueezy subscription, use switch-plan API
    const hasProviderSubscription = Boolean(subscription?.stripe_subscription_id || lemonSqueezyData?.id);
    if (hasProviderSubscription) {
      try {
        const response = await paymentAPI.switchPlan(targetPlan);
        await fetchBillingData();
        toast.success(response.data?.message || `Successfully switched to ${targetPlan} plan.`);
        return;
      } catch (switchError) {
        const errorData = switchError.response?.data;
        if (errorData?.requires_checkout) {
          // No provider subscription linked — fall through to checkout
        } else {
          toast.error(errorData?.error || 'Failed to switch plan');
          setProcessing(false);
          return;
        }
      }
    }

    // Fallback: create new checkout (for users without a provider subscription)
    try {
      const response = await paymentAPI.createCheckoutSession(targetPlan, 'billing');
      if (response.data?.checkout_url) {
        toast.success(`Redirecting to checkout for ${targetPlan}...`);
        window.location.href = response.data.checkout_url;
        return;
      }
      if (response.data?.checkout_required === false) {
        await fetchBillingData();
        toast.success('Plan updated successfully.');
        return;
      }
      toast.error('Checkout URL is missing');
      setProcessing(false);
    } catch (checkoutError) {
      toast.error(checkoutError.response?.data?.error || 'Failed to start checkout');
      setProcessing(false);
    }
  };

  const handleOpenCustomerPortal = async () => {
    // Prefer Lemon Squeezy subscription URLs (LS API returns urls.customer_portal); fallback to backend endpoint
    if (lemonSqueezyData?.urls?.customer_portal) {
      window.open(lemonSqueezyData.urls.customer_portal, '_blank');
      return;
    }
    try {
      const response = await paymentAPI.getCustomerPortal();
      if (response.data.portal_url) {
        window.open(response.data.portal_url, '_blank');
      } else {
        toast.error('Customer portal is not available');
      }
    } catch (error) {
      toast.error('Failed to open customer portal');
    }
  };

  const isSmartEnterpriseFeature = (feature = '') => {
    const normalized = String(feature).toLowerCase();
    return (
      normalized.includes('smart technician assignment') ||
      normalized.includes('smart technical assignment') ||
      normalized.includes('smart work-off delegation') ||
      normalized.includes('smart work off delegation')
    );
  };

  const getPlanDetails = (tier) => {
    const basicPrice = isBasicCampaignActive ? '£0' : '£6.99';
    const plans = {
      // Free tier for bike owners
      free: {
        name: 'Free',
        price: '£0',
        period: '/forever',
        features: [
          'View service history',
          'Digital Bike Passport',
          'Find bike shops',
          'Book appointments',
          'Rate & review shops'
        ],
        userType: 'bike_owner'
      },
      // Bike shop tiers
      basic: { 
        name: 'Basic', 
        price: basicPrice, 
        period: '/month', 
        features: [
          'Manual service logging',
          'Manual bike registration',
          'Customer management',
          'Digital Bike Passport',
          'Public map listing',
          'Customer reviews'
        ],
        userType: 'bike_shop'
      },
      professional: { 
        name: 'Professional', 
        price: '£20', 
        period: '/month', 
        features: [
          'All Basic features',
          'AI item extraction from photo receipt',
          'Frame number auto-matching'
        ],
        userType: 'bike_shop'
      },
      enterprise: { 
        name: 'Enterprise', 
        price: '£80', 
        period: '/month', 
        features: [
          'All Professional features',
          '1 month free trial',
          'Smart Technician Assignment (AI-powered)',
          'Smart Work-Off Delegation',
          'Online booking system',
          'Calendar & scheduling',
          'Team management',
          'Email & SMS marketing',
          'Analytics & reports',
          'Priority support'
        ],
        userType: 'bike_shop'
      },
      lifetime: { 
        name: 'Lifetime', 
        price: '£10,000', 
        period: 'one-time', 
        features: [
          'All Enterprise features',
          'One-time payment, forever',
          'Continuous updates',
          'Priority support',
          'Dedicated account manager'
        ],
        userType: 'bike_shop'
      },
      // Legacy tiers for backwards compatibility
      community: { name: 'Basic', price: basicPrice, period: '/month', features: ['Manual service logging', 'Bike Passport', 'Dashboard', 'Map listing'] },
      growth: { name: 'Professional', price: '£20', period: '/month', features: ['All Basic features', 'AI receipt extraction', 'Frame number matching'] },
      pro: { name: 'Professional', price: '£20', period: '/month', features: ['All features', 'Priority support'] },
      monthly: { name: 'Basic', price: basicPrice, period: '/month', features: ['Bike Passport', 'Service log', 'Dashboard', 'Map listing'] },
      yearly: { name: 'Enterprise', price: '£800', period: '/year', features: ['All Enterprise features', 'Save 2 months'] },
      test: { name: 'Test Plan', price: '£0', period: '/test', features: ['Full access', 'Demo purposes'] },
      trial: { name: 'Trial', price: '£0', period: '/14 days', features: ['Full access', 'Try before you buy'] },
    };
    return plans[tier] || plans.basic;
  };

  const getStatusBadge = (status, cancelAtPeriodEnd) => {
    if (cancelAtPeriodEnd) {
      return { text: 'Cancelling', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' };
    }
    
    const statusMap = {
      trial: { text: 'Trial', color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.15)' },
      active: { text: 'Active', color: '#4A8C18', bg: 'rgba(74, 140, 24, 0.15)' },
      cancelled: { text: 'Cancelled', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' },
      suspended: { text: 'Suspended', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' },
      past_due: { text: 'Past Due', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' },
    };
    return statusMap[status] || { text: status, color: '#6b7280', bg: 'rgba(107, 114, 128, 0.15)' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const currentPlan = getPlanDetails(subscription?.plan_tier);
  const statusBadge = getStatusBadge(subscription?.status, subscription?.cancel_at_period_end);
  const isMonthly = subscription?.plan_tier === 'monthly' || subscription?.plan_tier === 'basic';
  const isYearly = subscription?.plan_tier === 'yearly' || subscription?.plan_tier === 'pro';

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} pageTitle="Billing" darkMode={darkMode} onToggleDarkMode={onToggleDarkMode}>
        <div className="billing-loading">
          <div className="billing-loading-spinner"></div>
          <p>Loading billing information...</p>
        </div>
      </Layout>
    );
  }

  // Super Admin Billing Management View
  if (isSuperAdmin) {
    const filteredSubscriptions = filterStatus === 'all' 
      ? allSubscriptions 
      : allSubscriptions.filter(s => s.subscription?.status === filterStatus || (!s.subscription && filterStatus === 'none'));

    return (
      <Layout user={user} onLogout={onLogout} pageTitle="Billing Management" pageSubtitle="All company subscriptions and payments" darkMode={darkMode} onToggleDarkMode={onToggleDarkMode}>
        <div className={`billing-page ${isDarkMode ? 'dark' : 'light'}`}>
          
          {/* Stats Overview */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div className="billing-card" style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#3d5a20' }}>{billingStats.total_companies || 0}</div>
              <div style={{ fontSize: '13px', color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#6b7280' }}>Total Companies</div>
            </div>
            <div className="billing-card" style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#4A8C18' }}>{billingStats.active || 0}</div>
              <div style={{ fontSize: '13px', color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#6b7280' }}>Active</div>
            </div>
            <div className="billing-card" style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#8b5cf6' }}>{billingStats.trial || 0}</div>
              <div style={{ fontSize: '13px', color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#6b7280' }}>Trial</div>
            </div>
            <div className="billing-card" style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#ef4444' }}>{billingStats.cancelled || 0}</div>
              <div style={{ fontSize: '13px', color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#6b7280' }}>Cancelled</div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '24px',
            padding: '8px',
            background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.03)',
            borderRadius: '12px'
          }}>
            {[
              { id: 'subscriptions', label: 'Subscriptions', iconName: 'payment' },
              { id: 'payments', label: 'Payments', iconName: 'payment' },
              { id: 'invoices', label: 'Invoices', iconName: 'documents' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  background: activeTab === tab.id ? '#3d5a20' : 'transparent',
                  color: activeTab === tab.id ? '#ffffff' : (isDarkMode ? 'rgba(255,255,255,0.6)' : '#6b7280'),
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: '0.2s'
                }}
              >
                <Icon
                  name={tab.iconName}
                  color={activeTab === tab.id ? "primary" : "secondary"}
                  customColor={activeTab === tab.id ? '#ffffff' : undefined}
                  darkMode={isDarkMode}
                  size={16}
                />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Subscriptions Tab */}
          {activeTab === 'subscriptions' && (
            <div className="billing-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <h2 style={{ margin: 0, color: isDarkMode ? '#ffffff' : '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon name="payment" color="primary" darkMode={isDarkMode} size={20} />
                  Company Subscriptions
                </h2>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['all', 'active', 'trial', 'cancelled'].map(status => (
                    <button
                      key={status}
                      onClick={() => setFilterStatus(status)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '12px',
                        background: filterStatus === status ? '#3d5a20' : (isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'),
                        color: filterStatus === status ? '#ffffff' : (isDarkMode ? 'rgba(255,255,255,0.6)' : '#6b7280'),
                      }}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              
              {filteredSubscriptions.length === 0 ? (
                <p className="billing-empty-state">No subscriptions found</p>
              ) : (
                <div className="billing-table-wrapper">
                  <table className="billing-table">
                    <thead>
                      <tr>
                        <th>Company</th>
                        <th>Admin Email</th>
                        <th>Plan</th>
                        <th>Status</th>
                        <th>Trial Days</th>
                        <th>Next Billing</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSubscriptions.map((item) => {
                        const status = item.subscription?.status || 'none';
                        const statusStyle = getStatusBadge(status, item.subscription?.cancel_at_period_end);
                        return (
                          <tr key={item.company.id}>
                            <td style={{ fontWeight: '600' }}>{item.company.name}</td>
                            <td style={{ fontSize: '13px', color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#6b7280' }}>
                              {item.admin_email || '-'}
                            </td>
                            <td>
                              <span style={{
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                background: isDarkMode ? 'rgba(61, 90, 32, 0.2)' : 'rgba(61, 90, 32, 0.1)',
                                color: '#3d5a20'
                              }}>
                                {item.subscription?.plan_tier || item.company.subscription_tier || 'None'}
                              </span>
                            </td>
                            <td>
                              <span className="billing-table-badge" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                                {statusStyle.text}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {item.trial_days_remaining > 0 ? (
                                <span style={{ color: '#8b5cf6', fontWeight: '600' }}>{item.trial_days_remaining}</span>
                              ) : '-'}
                            </td>
                            <td>{item.subscription?.current_period_end ? formatDate(item.subscription.current_period_end) : '-'}</td>
                            <td style={{ fontSize: '13px', color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af' }}>
                              {formatDate(item.company.created_at)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Payments Tab */}
          {activeTab === 'payments' && (
            <div className="billing-card">
              <h2 style={{ marginBottom: '20px', color: isDarkMode ? '#ffffff' : '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="payment" color="primary" darkMode={isDarkMode} size={20} />
                All Payments
              </h2>
              {allPayments.length === 0 ? (
                <p className="billing-empty-state">No payments yet</p>
              ) : (
                <div className="billing-table-wrapper">
                  <table className="billing-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Company</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allPayments.map((payment) => {
                        const paymentStatus = payment.status === 'succeeded' 
                          ? { text: 'Paid', color: '#4A8C18', bg: 'rgba(26, 40, 17, 0.15)' }
                          : { text: payment.status, color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' };
                        return (
                          <tr key={payment.id}>
                            <td>{formatDate(payment.created_at)}</td>
                            <td style={{ fontWeight: '600' }}>{payment.company_name}</td>
                            <td className="billing-amount">
                              {payment.currency === 'GBP' ? '£' : payment.currency} {payment.amount?.toLocaleString()}
                            </td>
                            <td>
                              <span className="billing-table-badge" style={{ background: paymentStatus.bg, color: paymentStatus.color }}>
                                {paymentStatus.text}
                              </span>
                            </td>
                            <td className="billing-description">{payment.description || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Invoices Tab */}
          {activeTab === 'invoices' && (
            <div className="billing-card">
              <h2 style={{ marginBottom: '20px', color: isDarkMode ? '#ffffff' : '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="documents" color="primary" darkMode={isDarkMode} size={20} />
                All Invoices
              </h2>
              {allInvoices.length === 0 ? (
                <p className="billing-empty-state">No invoices yet</p>
              ) : (
                <div className="billing-table-wrapper">
                  <table className="billing-table">
                    <thead>
                      <tr>
                        <th>Invoice #</th>
                        <th>Company</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allInvoices.map((invoice) => {
                        const invoiceStatus = invoice.status === 'paid' 
                          ? { text: 'Paid', color: '#4A8C18', bg: 'rgba(26, 40, 17, 0.15)' }
                          : { text: invoice.status, color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' };
                        return (
                          <tr key={invoice.id}>
                            <td className="billing-invoice-number">{invoice.invoice_number}</td>
                            <td style={{ fontWeight: '600' }}>{invoice.company_name}</td>
                            <td>{formatDate(invoice.issue_date)}</td>
                            <td className="billing-amount">
                              {invoice.currency === 'GBP' ? '£' : invoice.currency} {invoice.amount?.toLocaleString()}
                            </td>
                            <td>
                              <span className="billing-table-badge" style={{ background: invoiceStatus.bg, color: invoiceStatus.color }}>
                                {invoiceStatus.text}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </Layout>
    );
  }

  // Regular User Billing View
  return (
    <Layout user={user} onLogout={onLogout} pageTitle="Billing & Subscription" pageSubtitle="Manage your subscription and payments" darkMode={darkMode} onToggleDarkMode={onToggleDarkMode}>
      <div className={`billing-page ${isDarkMode ? 'dark' : 'light'}`}>
        {isBasicCampaignActive && (
          <div
            className="billing-card"
            style={{
              marginBottom: '20px',
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(22, 101, 52, 0.25) 0%, rgba(20, 83, 45, 0.15) 100%)'
                : 'linear-gradient(135deg, #ecfdf5 0%, #dcfce7 100%)',
              border: isDarkMode
                ? '1px solid rgba(74, 222, 128, 0.35)'
                : '1px solid rgba(22, 163, 74, 0.35)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <Icon name="campaign" color="success" darkMode={isDarkMode} size={20} />
              <div>
                <div style={{ fontSize: '15px', fontWeight: '800', color: isDarkMode ? '#86efac' : '#166534' }}>
                  Company Campaign: Basic is free for everyone
                </div>
                <div style={{ fontSize: '13px', color: isDarkMode ? 'rgba(255,255,255,0.78)' : '#15803d', marginTop: '2px' }}>
                  Valid until {COMPANY_BASIC_FREE_CAMPAIGN_END_LABEL}. Switch to Basic without checkout.
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Current Subscription Overview */}
        <div className="billing-card billing-subscription-card">
          <div className="billing-card-header">
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon name="payment" color="primary" darkMode={isDarkMode} size={20} />
              Current Subscription
            </h2>
            {lemonSqueezyData?.urls?.customer_portal && (
              <button onClick={handleOpenCustomerPortal} className="billing-portal-btn">
                Manage on LemonSqueezy
              </button>
            )}
          </div>
          
          <div className="billing-subscription-content">
            <div className="billing-subscription-main">
              <div className="billing-plan-info">
                <div className="billing-plan-name">{currentPlan.name}</div>
                <div className="billing-plan-price">
                  <span className="price">{currentPlan.price}</span>
                  <span className="period">{currentPlan.period}</span>
                </div>
                <div className="billing-status-badge" style={{ background: statusBadge.bg, color: statusBadge.color }}>
                  {statusBadge.text}
                </div>
              </div>
              
              <div className="billing-subscription-details">
                {/* Trial Days Remaining */}
                {subscription?.status === 'trial' && trialDaysRemaining > 0 && (
                  <div className="billing-trial-info">
                    <div className="billing-trial-icon">
                      <Icon name="schedule" color="primary" darkMode={isDarkMode} size={24} />
                    </div>
                    <div className="billing-trial-content">
                      <div className="billing-trial-label">Trial Period</div>
                      <div className="billing-trial-days">
                        <span className="days-count">{trialDaysRemaining}</span>
                        <span className="days-text">days remaining</span>
                      </div>
                      <div className="billing-trial-end">
                        Ends on {formatDate(subscription?.trial_end)}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Next Billing Date */}
                {subscription?.current_period_end && subscription?.status === 'active' && (
                  <div className="billing-next-payment">
                    <div className="billing-detail-icon">
                      <Icon name="calendar" color="primary" darkMode={isDarkMode} size={24} />
                    </div>
                    <div className="billing-detail-content">
                      <div className="billing-detail-label">Next Payment</div>
                      <div className="billing-detail-value">{formatDate(subscription.current_period_end)}</div>
                    </div>
                  </div>
                )}
                
                {/* Plan Features */}
                <div className="billing-features">
                  <div className="billing-detail-label">Included Features:</div>
                  <ul className="billing-features-list">
                    {currentPlan.features.map((feature, idx) => (
                      <li
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: isSmartEnterpriseFeature(feature) ? '6px 10px' : '0',
                          borderRadius: isSmartEnterpriseFeature(feature) ? '8px' : '0',
                          background: isSmartEnterpriseFeature(feature)
                            ? (isDarkMode ? 'rgba(74, 140, 24, 0.2)' : 'rgba(61, 90, 32, 0.12)')
                            : 'transparent',
                          border: isSmartEnterpriseFeature(feature)
                            ? (isDarkMode ? '1px solid rgba(74, 140, 24, 0.45)' : '1px solid rgba(61, 90, 32, 0.35)')
                            : 'none'
                        }}
                      >
                        <Icon name="success" color="success" darkMode={isDarkMode} size={16} />
                        <span style={{ fontWeight: isSmartEnterpriseFeature(feature) ? '700' : '500' }}>{feature}</span>
                        {isSmartEnterpriseFeature(feature) && (
                          <span
                            className="selling-point-badge"
                            style={{
                              marginLeft: 'auto',
                              fontSize: '11px',
                              fontWeight: '700',
                              color: isDarkMode ? '#d9f99d' : '#3d5a20',
                              background: isDarkMode ? 'rgba(217, 249, 157, 0.12)' : 'rgba(61, 90, 32, 0.12)',
                              border: isDarkMode ? '1px solid rgba(217, 249, 157, 0.3)' : '1px solid rgba(61, 90, 32, 0.35)',
                              borderRadius: '999px',
                              padding: '2px 8px'
                            }}
                          >
                            Selling Point
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Subscription Actions */}
            <div className="billing-subscription-actions">
              {/* Change Plan - show when user has a subscription (or is on trial) and it's not cancelled */}
              {((subscription && !subscription.cancel_at_period_end && subscription.status !== 'cancelled') || (trialDaysRemaining > 0)) && (
                <button 
                  onClick={() => setShowSwitchModal(true)} 
                  className="billing-action-btn billing-action-switch"
                  disabled={processing}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  <Icon name="swap" customColor="#ffffff" darkMode={isDarkMode} size={16} />
                  Change Plan
                </button>
              )}
              
              {/* Cancel Subscription */}
              {(subscription?.status === 'active' || subscription?.status === 'trial') && !subscription?.cancel_at_period_end && (
                <button 
                  onClick={handleCancel} 
                  className="billing-action-btn billing-action-cancel"
                  disabled={processing}
                >
                  {processing ? 'Processing...' : 'Cancel Subscription'}
                </button>
              )}
              
              {/* Resume Subscription */}
              {subscription?.cancel_at_period_end && (
                <button 
                  onClick={handleResume} 
                  className="billing-action-btn billing-action-resume"
                  disabled={processing}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {processing ? 'Processing...' : (
                    <>
                      <Icon name="undo" customColor="#ffffff" darkMode={isDarkMode} size={16} />
                      Resume Subscription
                    </>
                  )}
                </button>
              )}
              
              {/* Upgrade from Trial */}
              {subscription?.status === 'trial' && (
                <div className="billing-upgrade-options">
                  <p className="billing-upgrade-text">Choose a plan to continue after your trial:</p>
                  <div className="billing-upgrade-buttons" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <button 
                      onClick={() => handleUpgrade('basic')} 
                      className="billing-action-btn billing-action-upgrade"
                      disabled={processing}
                    >
                      {isBasicCampaignActive
                        ? `Basic - FREE until ${COMPANY_BASIC_FREE_CAMPAIGN_END_LABEL} (Manual service logging)`
                        : 'Basic - £6.99/month (Manual service logging)'}
                    </button>
                    <button 
                      onClick={() => handleUpgrade('professional')} 
                      className="billing-action-btn billing-action-upgrade billing-action-recommended"
                      disabled={processing}
                    >
                      Professional - £20/month (AI receipt extraction)
                    </button>
                    <button 
                      onClick={() => handleUpgrade('enterprise')} 
                      className="billing-action-btn billing-action-upgrade"
                      disabled={processing}
                    >
                      Enterprise - £80/month (1 month free trial, billing integration + full suite)
                    </button>
                  </div>
                  {/* Lifetime option - subtle */}
                  <p style={{ marginTop: '16px', fontSize: '13px', color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af', textAlign: 'center' }}>
                    Looking for lifetime access? <a href="mailto:sales@frame-book.com" style={{ color: '#d97706' }}>Contact us</a>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment History */}
        <div className="billing-card">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name="documents" color="primary" darkMode={isDarkMode} size={20} />
            Payment History
          </h2>
          {payments.length === 0 ? (
            <p className="billing-empty-state">No payments yet</p>
          ) : (
            <div className="billing-table-wrapper">
              <table className="billing-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => {
                    const paymentStatus = payment.status === 'succeeded' 
                      ? { text: 'Paid', color: '#4A8C18', bg: 'rgba(26, 40, 17, 0.15)' }
                      : { text: payment.status, color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' };
                    return (
                      <tr key={payment.id}>
                        <td>{formatDate(payment.created_at)}</td>
                        <td className="billing-amount">
                          {payment.currency === 'GBP' ? '£' : payment.currency} {payment.amount.toLocaleString()}
                        </td>
                        <td>
                          <span className="billing-table-badge" style={{ background: paymentStatus.bg, color: paymentStatus.color }}>
                            {paymentStatus.text}
                          </span>
                        </td>
                        <td className="billing-description">{payment.description || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Invoices */}
        <div className="billing-card">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name="documents" color="primary" darkMode={isDarkMode} size={20} />
            Invoices
          </h2>
          {invoices.length === 0 ? (
            <p className="billing-empty-state">No invoices yet</p>
          ) : (
            <div className="billing-table-wrapper">
              <table className="billing-table">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => {
                    const invoiceStatus = invoice.status === 'paid' 
                      ? { text: 'Paid', color: '#4A8C18', bg: 'rgba(26, 40, 17, 0.15)' }
                      : { text: invoice.status, color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' };
                    return (
                      <tr key={invoice.id}>
                        <td className="billing-invoice-number">{invoice.invoice_number}</td>
                        <td>{formatDate(invoice.issue_date)}</td>
                        <td className="billing-amount">
                          {invoice.currency === 'GBP' ? '£' : invoice.currency} {invoice.amount.toLocaleString()}
                        </td>
                        <td>
                          <span className="billing-table-badge" style={{ background: invoiceStatus.bg, color: invoiceStatus.color }}>
                            {invoiceStatus.text}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Switch Plan Modal */}
        {showSwitchModal && (
          <div className="billing-modal-overlay" onClick={() => setShowSwitchModal(false)}>
            <div className="billing-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px' }}>
              <h3>Switch Your Plan</h3>
              <p>Choose the plan that works best for your workshop:</p>
              
              <div className="billing-modal-plans" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                {/* Basic */}
                <div 
                  className={`billing-modal-plan ${subscription?.plan_tier === 'basic' ? 'current' : ''}`}
                  onClick={() => subscription?.plan_tier !== 'basic' && handleSwitchPlan('basic')}
                  style={{ cursor: subscription?.plan_tier === 'basic' ? 'default' : 'pointer', padding: '20px' }}
                >
                  <div className="plan-name">Basic</div>
                  <div className="plan-price">
                    {isBasicCampaignActive ? (
                      <>
                        <span style={{ fontSize: '12px', fontWeight: '600', textDecoration: 'line-through', color: isDarkMode ? 'rgba(255,255,255,0.45)' : '#9ca3af', marginRight: '6px' }}>£6.99</span>
                        £0<span>/month</span>
                      </>
                    ) : (
                      <>£6.99<span>/month</span></>
                    )}
                  </div>
                  {isBasicCampaignActive && (
                    <div style={{ fontSize: '10px', color: '#4A8C18', fontWeight: '700', marginTop: '6px' }}>
                      Free campaign until {COMPANY_BASIC_FREE_CAMPAIGN_END_LABEL}
                    </div>
                  )}
                  <ul style={{ fontSize: '12px', color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#6b7280', textAlign: 'left', margin: '12px 0', padding: '0 0 0 16px' }}>
                    <li>Manual service logging</li>
                    <li>Bike registration</li>
                    <li>Customer management</li>
                    <li>Digital Bike Passport</li>
                  </ul>
                  {subscription?.plan_tier === 'basic' && <div className="current-badge">Current</div>}
                </div>
                
                {/* Professional */}
                <div 
                  className={`billing-modal-plan ${subscription?.plan_tier === 'professional' ? 'current' : ''}`}
                  onClick={() => subscription?.plan_tier !== 'professional' && handleSwitchPlan('professional')}
                  style={{ cursor: subscription?.plan_tier === 'professional' ? 'default' : 'pointer', padding: '20px' }}
                >
                  <div className="plan-name">Professional</div>
                  <div className="plan-price">£20<span>/month</span></div>
                  <ul style={{ fontSize: '12px', color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#6b7280', textAlign: 'left', margin: '12px 0', padding: '0 0 0 16px' }}>
                    <li>All Basic features</li>
                    <li style={{ fontWeight: '700', color: isDarkMode ? '#d9f99d' : '#3d5a20' }}>AI item extraction from photo receipt</li>
                    <li>Frame # auto-matching</li>
                  </ul>
                  <div style={{ fontSize: '10px', color: '#f59e0b', marginTop: '8px' }}>Requires 1 business day setup</div>
                  {subscription?.plan_tier === 'professional' && <div className="current-badge">Current</div>}
                </div>
                
                {/* Enterprise */}
                <div 
                  className={`billing-modal-plan ${subscription?.plan_tier === 'enterprise' ? 'current' : ''}`}
                  onClick={() => subscription?.plan_tier !== 'enterprise' && handleSwitchPlan('enterprise')}
                  style={{ cursor: subscription?.plan_tier === 'enterprise' ? 'default' : 'pointer', padding: '20px', border: '2px solid #3d5a20' }}
                >
                  <div className="plan-badge">Full Suite</div>
                  <div className="plan-name">Enterprise</div>
                  <div className="plan-price">£80<span>/month</span></div>
                  {subscription?.plan_tier !== 'enterprise' && (
                    <div style={{
                      margin: '8px 0',
                      padding: '6px 10px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: '700',
                      color: isDarkMode ? '#86efac' : '#166534',
                      background: isDarkMode ? 'rgba(22, 101, 52, 0.25)' : 'rgba(22, 163, 74, 0.1)',
                      border: isDarkMode ? '1px solid rgba(74, 222, 128, 0.35)' : '1px solid rgba(22, 163, 74, 0.3)',
                      textAlign: 'center',
                    }}>
                      1 month free trial included
                    </div>
                  )}
                  <ul style={{ fontSize: '12px', color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#6b7280', textAlign: 'left', margin: '12px 0', padding: '0 0 0 16px' }}>
                    <li>All Professional features</li>
                    <li style={{ fontWeight: '700', color: isDarkMode ? '#d9f99d' : '#3d5a20' }}>Smart Technician Assignment</li>
                    <li style={{ fontWeight: '700', color: isDarkMode ? '#d9f99d' : '#3d5a20' }}>Smart Work-Off Delegation</li>
                    <li>Online booking system</li>
                    <li>Calendar & scheduling</li>
                    <li>Team & technicians</li>
                    <li>Marketing tools</li>
                  </ul>
                  {subscription?.plan_tier === 'enterprise' && <div className="current-badge">Current</div>}
                </div>
              </div>
              
              <p style={{ marginTop: '16px', fontSize: '13px', color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>
                For Lifetime subscription or questions, <a href="mailto:sales@frame-book.com" style={{ color: '#d97706' }}>contact us</a>
              </p>
              
              <button className="billing-modal-close" onClick={() => setShowSwitchModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default BillingPage;
